import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Send, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LIFECYCLE_META = {
  trade_idea: { label: 'Trade Idea', color: '#6366f1' },
  doubt:      { label: 'Doubt',      color: '#f59e0b' },
  backtest:   { label: 'Backtest',   color: '#8b5cf6' },
  profit:     { label: 'Profit',     color: '#16c784' },
  loss:       { label: 'Loss',       color: '#ef4444' },
  mistake:    { label: 'Mistake',    color: '#f97316' },
};

/**
 * Direct share modal — pops up in-context on Backtest, Discipline, Trade pages.
 * Props:
 *   isOpen        boolean
 *   onClose       () => void
 *   attachments   [{ attach_type, snapshot }]   — pre-filled from the calling page
 *   defaultTitle  string
 *   defaultLifecycle  string   e.g. 'backtested'
 */
export default function ShareToForumModal({ isOpen, onClose, attachments = [], defaultTitle = '', defaultLifecycle = 'trade_idea' }) {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [lifecycle, setLifecycle] = useState('trade_idea');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [postedId, setPostedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle);
      setLifecycle(defaultLifecycle);
      setDescription('');
      setError('');
      setPostedId(null);
      setSubmitting(false);
    }
  }, [isOpen, defaultTitle, defaultLifecycle]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!title.trim()) { setError('Please add a title.'); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/forum/posts`, {
        title: title.trim(),
        description: description.trim() || title.trim(),
        lifecycle,
        instruments: [],
        tags: [],
        attachments,
      });
      setPostedId(res.data.id);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to share. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  };

  const sheet = {
    width: '100%', maxWidth: 520, borderRadius: '20px 20px 0 0',
    background: colors.cardBg || '#141927',
    border: '1px solid rgba(255,255,255,0.09)',
    padding: '20px 18px 32px',
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '9px 12px', fontSize: 13, color: '#eceef5', outline: 'none', boxSizing: 'border-box',
  };

  // ── Success state ─────────────────────────────────────────────────────────
  if (postedId) {
    return (
      <div style={overlay} onClick={onClose}>
        <div style={sheet} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(22,199,132,0.15)', border: '1px solid rgba(22,199,132,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Send size={20} color="#16c784" />
            </div>
            <p style={{ fontSize: 16, fontWeight: 800, color: '#eceef5', marginBottom: 6 }}>Shared to Strategy Feed!</p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 20 }}>Your strategy is now visible to the community.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '11px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Close
              </button>
              <button
                onClick={() => { onClose(); navigate(`/forum/${postedId}`); }}
                style={{ flex: 1, padding: '11px', borderRadius: 12, background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ExternalLink size={13} /> View Post
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Share form ─────────────────────────────────────────────────────────────
  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={e => e.stopPropagation()}>
        {/* Handle bar */}
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', margin: '0 auto 16px' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <p style={{ fontSize: 15, fontWeight: 800, color: '#eceef5' }}>Share to Strategy Feed</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={16} color="rgba(255,255,255,0.4)" />
          </button>
        </div>

        {/* Attachment preview pills */}
        {attachments.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
            {attachments.map((a, i) => {
              const labels = { backtest: 'Backtest Result', pnl_summary: 'P&L Summary', discipline: 'Discipline Score', trade_history: 'Trade Records', capital_tier: 'Capital Tier', decision_engine: 'Decision Engine' };
              const colors2 = { backtest: '#6366f1', pnl_summary: '#16c784', discipline: '#f59e0b', trade_history: '#06b6d4', capital_tier: '#8b5cf6', decision_engine: '#10b981' };
              const c = colors2[a.attach_type] || '#6366f1';
              return (
                <span key={i} style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: c + '18', color: c, border: `1px solid ${c}35` }}>
                  {labels[a.attach_type] || a.attach_type} attached
                </span>
              );
            })}
          </div>
        )}

        {/* Title */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>Title *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What's this strategy about?" style={inputStyle} />
        </div>

        {/* Description (optional) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 5 }}>
            Notes <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
          </label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Any observations, edge, or lessons to share…" rows={3}
            style={{ ...inputStyle, resize: 'none', lineHeight: 1.55 }} />
        </div>

        {/* Stage */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 6 }}>Stage</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(LIFECYCLE_META).map(([key, lm]) => (
              <button key={key} onClick={() => setLifecycle(key)}
                style={{
                  padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: lifecycle === key ? lm.color + '22' : 'rgba(255,255,255,0.04)',
                  color: lifecycle === key ? lm.color : 'rgba(255,255,255,0.35)',
                  border: `1px solid ${lifecycle === key ? lm.color + '50' : 'rgba(255,255,255,0.1)'}`,
                }}>
                {lm.label}
              </button>
            ))}
          </div>
        </div>

        {error && <p style={{ fontSize: 11, color: '#ef4444', marginBottom: 10, fontWeight: 600 }}>{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={submitting || !title.trim()}
          style={{
            width: '100%', padding: '13px', borderRadius: 13, cursor: 'pointer',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            border: 'none', fontSize: 14, fontWeight: 800, color: '#fff',
            opacity: submitting || !title.trim() ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          <Send size={14} />
          {submitting ? 'Sharing…' : 'Publish to Feed'}
        </button>
      </div>
    </div>
  );
}
